// Sport-aware pitch drawer dispatcher. One public entry point —
// `drawPitch(ctx, pitch, transform, w, h, opts)` — that picks the right
// sport-specific drawer via `pitch.sport`. Individual sport drawers stay
// internal; callers only see `drawPitch` + the `PitchInput` shape.
import type { PitchCoordinates, PitchTransform } from '../types'
import { drawAmericanFootballPitch } from './american-football/draw'
import { drawCanadianFootballPitch } from './canadian-football/draw'
import { drawSoccerPitch } from './soccer/draw'
import type { PitchDrawOptions, SportPitchDrawer } from './types'

export type { PitchDrawOptions } from './types'

export interface PitchInput {
  /** Sport discriminator as the backend returns it — e.g. "Soccer",
   *  "AmericanFootball", "CanadianFootball". Internally normalised. */
  sport: string
  coordinates: PitchCoordinates
}

// Sport key normalisation — lower-cased, spaces + hyphens + underscores
// collapsed. Mirrors `normaliseSportType` in @frontend/core but kept local
// so libs/pitch has no dependency on the reporting domain.
function normaliseSportKey(sport: string): string {
  return sport.toLowerCase().replace(/[\s\-_]/g, '')
}

const drawers: Record<string, SportPitchDrawer> = {
  soccer: drawSoccerPitch,
  football: drawSoccerPitch, // ambiguous "Football" defaults to soccer
  americanfootball: drawAmericanFootballPitch,
  canadianfootball: drawCanadianFootballPitch
}

/**
 * Draw a pitch. Resolves the sport-specific drawer via `pitch.sport` and
 * falls back to soccer for unknown sports — callers that pass an
 * unrecognised sport still get something on-screen.
 */
export function drawPitch(
  ctx: CanvasRenderingContext2D,
  pitch: PitchInput,
  transform: PitchTransform,
  canvasWidth: number,
  canvasHeight: number,
  options?: PitchDrawOptions
): void {
  const key = normaliseSportKey(pitch.sport ?? '')
  const drawer = drawers[key] ?? drawSoccerPitch
  drawer(
    ctx,
    pitch.coordinates,
    transform.worldToCanvas,
    canvasWidth,
    canvasHeight,
    options
  )
}
